import { memo, useEffect, useState, useRef } from 'react';
import { UploadProps, Upload } from 'antd';
import AutoSizeImage from './auto-size-image';
import UploadPictureExample from '@/sub-luca-plugin/components/picture-crop/upload-picture-example';
import CombineLoading from './combine-loading';
import { SortableContainer, SortableElement, SortEndHandler } from 'react-sortable-hoc';
import { uploadFile, previewPicture } from '@/sub-luca-plugin/utils';
import { toastInQa } from '@/sub-luca-plugin/utils/qa';
import { eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import { luca_ask_ocr } from '@/sub-luca-plugin/apis/qa';
import { getQaStore } from '@/sub-luca-plugin/store/qa';
import { useControlDrawerTransform } from '@/sub-luca-plugin/utils/custom-hooks';
import { INoticeData } from '@/sub-luca-plugin/types';
import classNames from 'classnames';
import './index.scss';

const Picture = SortableElement<{ id: number; url: string }>(({ url, id }: { id: number; url: string }) => {
    return (
        <li className="combine-pictures-drawer__sortable-element">
            <div className="combine-pictures-drawer__picture-delete" id={`delete-${id}`}></div>
            <div className="combine-pictures-drawer__picture-container">
                <AutoSizeImage className="combine-pictures-drawer__picture" src={url} />
            </div>
        </li>
    );
});

const PicturesContainer = SortableContainer<{ urls: string[] }>(({ urls }: { urls: string[] }) => {
    // 上传文件格式校验
    const handleBeforeUpload: UploadProps['beforeUpload'] = file => {
        const types = ['image/png', 'image/jpg', 'image/jpeg'];
        if (types.includes(file.type)) return true;
        toastInQa('仅支持上传png/jpg/jpeg格式的图片');
        return false;
    };

    const handleUploadChange: UploadProps['customRequest'] = info => {
        const localUrl = URL.createObjectURL(info.file as File);
        // 跳转
        eventBusExample.publish('PictureCrop', { type: 'crop', params: { url: localUrl } });
    };

    return (
        <ul className="combine-pictures-drawer__sortable-container">
            {urls.map((url, index) => (
                <Picture key={url} id={index} url={url} index={index} />
            ))}
            {urls.length < 6 && (
                <Upload
                    accept=".jpg, .jpeg, .png"
                    showUploadList={false}
                    beforeUpload={handleBeforeUpload}
                    customRequest={handleUploadChange}
                >
                    <div className="combine-pictures-drawer__upload-picture">相册导入</div>
                </Upload>
            )}
        </ul>
    );
});

const CombinePicturesDrawer = () => {
    const exampleMaxHeight = useRef('0');
    const initParams = useRef<Record<string, any>>({});
    const isHomeOcr = !!initParams.current.lucaId;
    const [exampleInfo, setExampleInfo] = useState({ visible: false, title: '' });
    const [pictures, setPictures] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const { style, showDrawer, hideDrawer } = useControlDrawerTransform({ needTransition: !isHomeOcr });

    const revokeObjectURLs = (urls: string[]) => {
        urls.forEach(url => URL.revokeObjectURL(url));
    };

    const loadImg = (url: string): Promise<HTMLImageElement> =>
        new Promise(resolve => {
            const img = new Image();
            img.setAttribute('crossOrigin', 'anonymous');
            img.onload = () => resolve(img);
            img.src = url;
        });

    const getDrawInfo = async (urls: string[]) => {
        type TDrawInfo = {
            canvasWidth: number;
            canvasHeight: number;
            drawImageParamsList: Array<[HTMLImageElement, number, number, number, number]>;
        };
        const drawInfo: TDrawInfo = { canvasWidth: 0, canvasHeight: 0, drawImageParamsList: [] };
        const imgs = await Promise.all(urls.map(url => loadImg(url)));
        return imgs.reduce((info, img) => {
            info.drawImageParamsList.push([img, 0, info.canvasHeight, img.width, img.height]);
            info.canvasWidth = Math.max(info.canvasWidth, img.width);
            info.canvasHeight += img.height;
            return info;
        }, drawInfo);
    };

    const getCdnOfCompositePicture = (urls: string[]): Promise<string> =>
        new Promise(async resolve => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            const { canvasWidth, canvasHeight, drawImageParamsList } = await getDrawInfo(urls);
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            drawImageParamsList?.forEach(params => ctx.drawImage(...params));
            canvas.toBlob(
                async blob => {
                    if (!blob) return;
                    const file = new File([blob], 'image.png', { type: 'image/png', lastModified: Date.now() });
                    console.log(`合成图大小：${Math.round((file.size / 1024 / 1024) * 10) / 10}M；`);
                    const url = await uploadFile(file);
                    console.log(`图片地址：${url}`);
                    resolve(url);
                },
                'image/jpg',
                0.7
            );
        });

    const identifyContentFail = () => {
        toastInQa('识别失败，请稍后重试~');
        setLoading(false);
    };

    const handleSearchQuestionsAfterComposite = async () => {
        if (!pictures.length || loading) return;
        setLoading(true);
        const cdnUrl = await getCdnOfCompositePicture(pictures);
        if (!cdnUrl) {
            identifyContentFail();
            return;
        }
        // 由于合并的图片较大，这里提前加载该资源，防止关闭弹层时，合并的图片显示空白
        const loadImgPromise = loadImg(cdnUrl);
        const { projectId, subjectId, twoLevelSubjectId } = getQaStore()!.qaConfig;
        try {
            const { result: content, status } = await luca_ask_ocr({ imageUrl: cdnUrl, projectId, subjectId, twoLevelSubjectId });
            if (status !== 0) {
                identifyContentFail();
                return;
            }
            eventBusExample.publish('DialogueArea', {
                type: 'searchQuestionsByOcrContent',
                params: { recordType: 40, text: { belongType: 0, content: content.slice(0, 5000) }, images: [cdnUrl] },
                callbacks: {
                    finish: (success: boolean) => {
                        if (success) {
                            getQaStore()?.updateQaConfig({ showSearchQuestionsByOcrBtn: false, showWhetherSolve: false });
                            loadImgPromise.then(handleCloseDrawer);
                        } else {
                            setLoading(false);
                        }
                    },
                },
            });
        } catch (error) {
            identifyContentFail();
        }
    };

    const handleProjectSubjectSelect = () => {
        if (!pictures.length) return;
        eventBusExample.publish('QaInitOverlay', {
            type: 'initByLucaId',
            params: { ...initParams.current, allowChangeDeterminedIds: true, showProjectSelectCloseBtn: true },
            callbacks: {
                // 开始合并搜索
                startCombineSearch: async (aiAskOnOff: boolean) => {
                    if (aiAskOnOff) {
                        handleSearchQuestionsAfterComposite();
                    } else {
                        setLoading(true);
                        const cdnUrl = await getCdnOfCompositePicture(pictures);
                        eventBusExample.publish('DialogueTool', { type: 'echo', params: { images: [cdnUrl] } });
                        handleCloseDrawer();
                    }
                },
            },
        });
    };

    const handleSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
        if (oldIndex === newIndex) {
            previewPicture(pictures[oldIndex]);
            return;
        }
        const newPictures = [...pictures];
        [newPictures[oldIndex], newPictures[newIndex]] = [newPictures[newIndex], newPictures[oldIndex]];
        setPictures(newPictures);
    };

    const handleShouldCancelStart = (ev: any) => {
        const id: string = ev.target.id || '';
        if (id.includes('delete')) {
            setPictures(oldList => oldList.filter((_, index) => index !== +id.split('-')[1]));
            return true;
        }
        return false;
    };

    const handleCloseDrawer = () => {
        initParams.current = {};
        setLoading(false);
        hideDrawer();
        setPictures([]);
        handleCloseExample();
        revokeObjectURLs(pictures);
    };

    const handleShowExample = (title: string) => setExampleInfo({ visible: true, title });

    const handleCloseExample = () => setExampleInfo({ visible: false, title: '' });

    const handleClickCloseIcon = () => {
        if (isHomeOcr) {
            eventBusExample.publish('QaDrawer', { type: 'close' });
            setTimeout(handleCloseDrawer, 500);
        } else {
            handleCloseDrawer();
        }
    };

    useEffect(() => {
        eventBusExample.subscribe('CombinePicturesDrawer', ({ type, params = {} }: INoticeData) => {
            const { url, lucaId, defaultIds } = params;
            switch (type) {
                case 'crop_success':
                    lucaId && (initParams.current = { lucaId, defaultIds });
                    setPictures(oldUrls => oldUrls.concat(url));
                    showDrawer();
                    break;
                default:
                    handleCloseDrawer();
                    break;
            }
        });
        exampleMaxHeight.current = document.body.clientHeight - 48 + 'px';
    }, []);

    return (
        <>
            {loading && <CombineLoading />}
            <div className="combine-pictures-drawer" style={style}>
                <div className="combine-pictures-drawer__header">
                    <div className="combine-pictures-drawer__title">拍题答疑</div>
                    <div className="combine-pictures-drawer__example" onClick={() => handleShowExample('拍题示例')}>
                        示例
                    </div>
                    <div className="combine-pictures-drawer__close" onClick={handleClickCloseIcon} />
                </div>
                <div className="combine-pictures-drawer__drag-tips">长按可拖动排序</div>
                <PicturesContainer axis="xy" urls={pictures} onSortEnd={handleSortEnd} shouldCancelStart={handleShouldCancelStart} />
                <footer className="combine-pictures-drawer__footer">
                    <div
                        className={classNames(
                            'combine-pictures-drawer__merge-btn',
                            !pictures.length && 'combine-pictures-drawer__merge-btn--disabled'
                        )}
                        onClick={() => {
                            isHomeOcr ? handleProjectSubjectSelect() : handleSearchQuestionsAfterComposite();
                        }}
                    >
                        {pictures.length > 1 ? '合并搜题' : '立即搜题'}
                    </div>
                </footer>
            </div>
            <UploadPictureExample
                className="combine-pictures-drawer__upload-example"
                maxHeight={exampleMaxHeight.current}
                {...exampleInfo}
                onClose={handleCloseExample}
                showMode="translateY"
            />
        </>
    );
};

export default memo(CombinePicturesDrawer);
